import { logger } from '../utils/logger';
import { API_BASE_URL, fetchWithTimeout, getAuthHeaders } from './heygenService';

/**
 * Queue System for Avatar Sessions
 *
 * Uses SQS FIFO queue for guaranteed ordering and fair queue access.
 * Backend manages the queue in SQS + DynamoDB for status tracking.
 */

export interface ActiveSessionsResponse {
  activeCount: number;
  limit: number;
  available: boolean;
}

export interface QueueCheckResponse {
  canStart: boolean;
  inQueue: boolean;
  position: number;
  totalInQueue: number;
}

export interface QueueStatusResponse {
  position: number;
  totalInQueue: number;
  ready: boolean;
  expiresAt?: string; // ISO timestamp, present when ready=true
  countdown?: number; // Seconds remaining to claim slot
}

/**
 * Get current active session count from LiveAvatar via backend proxy
 */
export const getActiveSessionCount = async (): Promise<ActiveSessionsResponse> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/heygen/active-sessions`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      // Fallback if endpoint not implemented yet
      logger.warn('HeyGenService', 'Failed to get active sessions, assuming available', {
        status: response.status,
      });
      return { activeCount: 0, limit: 100, available: true };
    }

    return await response.json();
  } catch (error) {
    logger.warn('HeyGenService', 'Error fetching active sessions', error);
    // Safe fallback to allow flow to continue if service is down
    return { activeCount: 0, limit: 100, available: true };
  }
};

/**
 * Check queue state and join if needed - MAIN ENTRY POINT
 *
 * This is the fair queue implementation:
 * - If queue is empty AND slots available → canStart: true
 * - Otherwise → joined queue, returns position (X of Y)
 */
export const checkQueue = async (): Promise<QueueCheckResponse> => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/heygen/queue/check`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({ error: 'Failed to check queue' }))) as {
      error?: string;
    };
    throw new Error(errorData.error ?? 'Failed to check queue');
  }

  return (await response.json()) as QueueCheckResponse;
};

/**
 * Get current queue status for a user
 * Returns position (X of Y) and whether it's their turn (ready=true)
 */
export const getQueueStatus = async (): Promise<QueueStatusResponse | null> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/heygen/queue/status`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      // If not found, user is not in queue
      if (response.status === 404) {
        return null;
      }
      const errorData = (await response
        .json()
        .catch(() => ({ error: 'Failed to get queue status' }))) as {
        error?: string;
      };
      throw new Error(errorData.error ?? 'Failed to get queue status');
    }

    return (await response.json()) as QueueStatusResponse;
  } catch (error) {
    logger.warn('HeyGenService', 'Error fetching queue status', error);
    return null;
  }
};

/**
 * Leave the waiting queue voluntarily
 * Uses sendBeacon for browser close scenarios to ensure cleanup
 */
export const leaveQueue = async (): Promise<void> => {
  try {
    await fetch(`${API_BASE_URL}/heygen/queue/leave`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
  } catch (error) {
    // Low severity, just log
    logger.debug('HeyGenService', 'Leave queue failed', error);
  }
};

/**
 * Leave queue using sendBeacon (for beforeunload/visibilitychange)
 * This is fire-and-forget for browser tab close cleanup
 */
export const leaveQueueBeacon = (): boolean => {
  const url = `${API_BASE_URL}/heygen/queue/leave`;

  try {
    fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
      keepalive: true,
    });
    return true;
  } catch (error) {
    logger.debug('HeyGenService', 'Leave queue beacon failed', error);
    return false;
  }
};

/**
 * End an active live avatar session and trigger credit consumption on the backend.
 * Calls the dedicated session-end endpoint (separate from queue management).
 */
export const endSession = async (): Promise<void> => {
  try {
    await fetch(`${API_BASE_URL}/heygen/session-end`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
  } catch (error) {
    logger.debug('HeyGenService', 'End session failed', error);
  }
};

/**
 * End session using sendBeacon (for beforeunload when session is active).
 * Fire-and-forget to ensure credit consumption even on browser close.
 */
export const endSessionBeacon = (): boolean => {
  const url = `${API_BASE_URL}/heygen/session-end`;

  try {
    fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
      keepalive: true,
    });
    return true;
  } catch (error) {
    logger.debug('HeyGenService', 'End session beacon failed', error);
    return false;
  }
};

/**
 * Session ping — called every 6s during active streaming.
 * Provides a liveness signal to the backend (billing-enabled) and returns current balance.
 */
export interface HeartbeatResponse {
  creditsBilled: number;
  totalBilledThisSession: number;
  remaining: { subscription: number; addon: number; total: number };
  shouldEnd: boolean;
}

/**
 * SESSION HEARTBEAT (Billing Signal + Liveness Ping)
 *
 * This call serves two purposes:
 * 1. BILLING: The backend deducts 0.2 credits per completed 6s bucket since the last billing.
 * 2. LIVENESS: Updates lastHeartbeatAt so the backend watchdog knows the client is alive.
 *
 * If this call STOPS (tab closed/network failure), the watchdog kills the session
 * and bills for the remaining gap.
 * If credits are exhausted, the response sets shouldEnd=true so the frontend closes.
 */
export const sendSessionPing = async (): Promise<HeartbeatResponse | null> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/heygen/session-heartbeat`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      if (response.status === 404) {
        // Session was closed server-side (cleanup Lambda or credit exhaustion).
        // Return shouldEnd so the frontend stops the stream.
        logger.warn('HeyGenService', 'Ping 404 — session closed server-side');
        return {
          creditsBilled: 0,
          totalBilledThisSession: 0,
          remaining: { subscription: 0, addon: 0, total: 0 },
          shouldEnd: true,
        };
      }
      logger.warn('HeyGenService', 'Ping failed', { status: response.status });
      return null;
    }

    return (await response.json()) as HeartbeatResponse;
  } catch (error) {
    logger.warn('HeyGenService', 'Ping error', error);
    return null;
  }
};

/**
 * Claim the slot when it's user's turn
 * Returns success if slot is still available, false if expired or taken
 */
export const claimQueueSlot = async (): Promise<{ success: boolean; reason?: string }> => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/heygen/queue/claim`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const errorData = (await response
      .json()
      .catch(() => ({ success: false, reason: 'unknown' }))) as {
      success: boolean;
      reason?: string;
    };
    return { success: false, reason: errorData.reason ?? 'unknown' };
  }

  return (await response.json()) as { success: boolean; reason?: string };
};
